import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {injectIntl} from 'react-intl';
import ReactDOM from 'react-dom';
import AddedLabel from './added_label';

class LabelPicker extends Component {
	constructor(props) {
		super(props);
		this.state = {
			input: '',
			matchingLabels: [],
			showDropdown: true,
			isManagingFocus: false,
			dropdownHeight: 250,
		};
		this.collapseDropdown = this.collapseDropdown.bind(this);
	}

	componentDidMount() {
		document.addEventListener('scroll', this.collapseDropdown, true);
	}

	componentDidUpdate(prevProps, prevState) {
		if ((!prevState.isManagingFocus && this.state.isManagingFocus) || (this.dropdown && prevState.searchCriteria !== this.state.searchCriteria)) {
			const dropdownNode = this.dropdown.getBoundingClientRect();
			this.setState({
				dropdownHeight: dropdownNode.height,
			});
		}
	}

	componentWillUnmount() {
		document.removeEventListener('scroll', this.collapseDropdown, true);
	}

	collapseDropdown(e) {
		if (e.target && e.target.id === 'labels-dropdown') return;
		this.labelPickerInput.blur();
	}

	clearState() {
		this.setState({
			input: '',
		});
	}

	removeLabelHandler(label) {
		this.props.setLabels([...this.props.addedLabels.filter(l => l !== label)]);
	}

	addLabelHandler(label) {
		// If already added, return
		if (this.props.addedLabels.some(l => l === label.value)) return;

		this.props.setLabels([...this.props.addedLabels, label.value]);

		// HACK render the component again in order to position the label dropdown correctly
		setTimeout(() => {
			this.forceUpdate();
		});
	}

	createNewLabelHandler(val) {
		this.props.createNewLabel(val, () => {
			setTimeout(() => {
				this.forceUpdate();
			});
		});
		if (this.props.addedLabels.some(l => l === val)) return;
		this.props.setLabels([...this.props.addedLabels, val]);
	}

	keyPressInDropdownHandler(e, label) {
		if (this.props.projectLocked) return;
		if (e.key === 'Enter') {
			this.addLabelHandler(label);
			this.clearState();
			this.labelPickerInput.focus();
		} else if (e.key === 'Escape') {
			this.clearState();
			e.currentTarget.blur();
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (e.currentTarget.nextElementSibling !== null) e.currentTarget.nextElementSibling.focus();
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (e.altKey) {
				this.setState({showDropdown: false});
				this.labelPickerInput.focus();
				return;
			}
			if (e.currentTarget.previousElementSibling !== null) e.currentTarget.previousElementSibling.focus();
			else this.labelPickerInput.focus();
		}
	}

	onMouseDownInDropdownHandler(e, label) {
		e.preventDefault();
		this.addLabelHandler(label);
		this.clearState();
		this.labelPickerInput.focus();
	}

	onLabelPickerInputChange(val) {
		this.setState({
			input: val,
		});
	}

	getLabelsMatchingAndNotAdded(val) {
		if (!this.props.possibleLabels) return [];
		return this.props.possibleLabels
			.filter(p => !this.props.addedLabels.includes(p.value) && (val === '' || p.label.toLowerCase().startsWith(val.toLowerCase())))
			.sort((l1, l2) => {
				if (l1.label < l2.label) return -1;
				return 1;
			});
	}

	keyPressInInputHandler(e) {
		if (this.props.projectLocked) return;
		// Check if value is non-empty and that there are no exact matches
		if (e.key === 'Enter' && this.state.input.trim() !== '' && !this.state.matchingLabels.some(p => p.label === this.state.input.trim())) {
			this.createNewLabelHandler(this.state.input);
			this.clearState();
		} else if (e.key === 'Escape') {
			this.clearState();
			this.labelPickerInput.blur();
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (e.altKey) {
				this.setState({showDropdown: true});
				return;
			}
			if (this.matchingLabelsDropdown.children.length !== 0) this.matchingLabelsDropdown.children[0].focus();
		} else if (e.key === 'ArrowUp' && e.altKey) {
			this.setState({showDropdown: false});
			this.labelPickerInput.focus();
			return;
		}
	}

	isInputInFocus(e) {
		const matchingDropdownIsFocused = this.matchingLabelsDropdown.contains(e.relatedTarget) || this.matchingLabelsDropdown.contains(document.activeElement) || this.matchingLabelsDropdown.contains(e.explicitOriginalTarget);

		const labelInputIsFocused = this.labelPickerInput.contains(e.relatedTarget) || this.labelPickerInput.contains(document.activeElement) || this.labelPickerInput.contains(e.explicitOriginalTarget);

		return matchingDropdownIsFocused || labelInputIsFocused;
	}

	onFocus(e) {
		// Clear the timeout so we dont collapse the dropdown
		clearTimeout(this._timeoutID);
		if (!this.state.isManagingFocus) {
			this.setState({
				isManagingFocus: true,
				showDropdown: true,
			});
		}
	}

	onBlur(e) {
		// Set a timeout to collapse the dropdown if we dont get focus within this tick
		this._timeoutID = setTimeout(() => {
			if (this.state.isManagingFocus) {
				this.setState({
					isManagingFocus: false,
				});
			}
		}, 0);
	}

	renderLabelPickerInput() {
		if (this.props.projectLocked) return null;
		const matchingLabels = this.getLabelsMatchingAndNotAdded(this.state.input);
		let dropdownStyle = {};
		let showOnTop = false;
		if (this.wrapperDivRef) {
			this.offset = this.wrapperDivRef.getBoundingClientRect();
		}
		if (this.offset) {
			if (this.offset.top + this.offset.height + this.state.dropdownHeight > window.innerHeight) {
				showOnTop = true;
				dropdownStyle = {
					bottom: window.innerHeight - this.offset.top,
					left: this.offset.left,
					marginBottom: 0,
				};
			} else {
				dropdownStyle = {
					top: this.offset.top + this.offset.height,
					left: this.offset.left,
				};
			}
		}
		if (this.state.matchingLabels.length !== 0 && this.state.showDropdown) {
			dropdownStyle.height = matchingLabels.length * 25 > this.props.dropdownMaxHeight ? this.props.dropdownMaxHeight : matchingLabels.length * 25 + 'px';
		}

		return (
			<div className={'input-container' + (this.state.isManagingFocus ? ' focused' : '')} onFocus={this.onFocus.bind(this)} onBlur={this.onBlur.bind(this)} ref={div => (this.wrapperDivRef = div)}>
				{this.props.addInfoText ? (
					<div className="add-label-controls-wrapper">
						<input
							className={this.state.matchingLabels.length !== 0 ? 'label-picker-dropdown-showing' + (showOnTop ? ' on-top' : '') : 'label-picker'}
							type="text"
							onFocus={() => this.setState({matchingLabels: this.getLabelsMatchingAndNotAdded(this.state.input)})}
							onBlur={e => {
								if (!this.isInputInFocus(e)) this.setState({input: '', matchingLabels: []});
							}}
							value={this.state.input}
							onChange={e => this.onLabelPickerInputChange(e.target.value)}
							onKeyDown={e => this.keyPressInInputHandler(e)}
							ref={labelPickerInput => {
								this.labelPickerInput = labelPickerInput;
							}}
							disabled={this.props.projectLocked}
						/>
						<span className="add-label-button-label">{this.props.intl.formatMessage({id: 'bulk_card_update_modal.add_labels'})}</span>
					</div>
				) : (
					<input
						className={this.state.matchingLabels.length !== 0 ? 'label-picker-dropdown-showing' + (showOnTop ? ' on-top' : '') : 'label-picker'}
						type="text"
						onFocus={() => this.setState({matchingLabels: this.getLabelsMatchingAndNotAdded(this.state.input)})}
						onBlur={e => {
							if (!this.isInputInFocus(e)) this.setState({input: '', matchingLabels: []});
						}}
						value={this.state.input}
						onChange={e => this.onLabelPickerInputChange(e.target.value)}
						onKeyDown={e => this.keyPressInInputHandler(e)}
						ref={labelPickerInput => {
							this.labelPickerInput = labelPickerInput;
						}}
						disabled={this.props.projectLocked}
					/>
				)}
				<div className="add-icon" />
				{ReactDOM.createPortal(
					<div className={'dropdown-hider'} style={dropdownStyle} ref={ul => (this.dropdown = ul)}>
						<div
							id="labels-dropdown"
							className={
								'matching-labels-dropdown ' +
								(this.state.matchingLabels.length !== 0 && matchingLabels.length !== 0 && this.state.showDropdown ? (showOnTop ? 'matching-labels-dropdown-show-on-top' : 'matching-labels-dropdown-show') : 'visually-hidden')
							}
							style={
								this.state.matchingLabels.length !== 0 && matchingLabels.length !== 0 && this.state.showDropdown
									? {
											height: matchingLabels.length * 25 > this.props.dropdownMaxHeight ? this.props.dropdownMaxHeight : matchingLabels.length * 25 + 'px',
									  }
									: {}
							}
							ref={matchingLabelsDropdown => {
								this.matchingLabelsDropdown = matchingLabelsDropdown;
							}}
						>
							{matchingLabels.map(ml => (
								<button
									id={'label_' + ml.value}
									key={ml.value}
									className="matching-label"
									onBlur={e => {
										if (!this.isInputInFocus(e)) this.setState({input: '', matchingLabels: []});
									}}
									onKeyDown={e => this.keyPressInDropdownHandler(e, ml)}
									onMouseDown={e => this.onMouseDownInDropdownHandler(e, ml)}
									tabIndex="-1"
								>
									{ml.label}
								</button>
							))}
						</div>
					</div>,
					document.querySelector('#root-portal-container')
				)}
			</div>
		);
	}

	render() {
		const addedLabels = this.props.placeInputFirst ? this.props.addedLabels : this.props.addedLabels.slice(0).reverse();
		return (
			<span className="label-picker-container">
				{this.props.placeInputFirst ? this.renderLabelPickerInput() : null}
				<div className="added-labels-wrapper">
					{addedLabels.map((al, idx) => {
						const label = this.props.possibleLabels.find(l => l.value === al);
						if (label) {
							return (
								<AddedLabel
									key={idx}
									label={label}
									onDeleteClick={() => {
										this.removeLabelHandler(al);
									}}
									disabled={this.props.projectLocked}
								/>
							);
						}
						return null;
					})}
				</div>
				{this.props.placeInputFirst ? null : this.renderLabelPickerInput()}
			</span>
		);
	}
}

LabelPicker.propTypes = {
	addedLabels: PropTypes.arrayOf(PropTypes.string).isRequired,
	possibleLabels: PropTypes.arrayOf(
		PropTypes.shape({
			value: PropTypes.string,
			label: PropTypes.string,
			color: PropTypes.string,
		})
	).isRequired,
	setLabels: PropTypes.func.isRequired,
	createNewLabel: PropTypes.func.isRequired,
	placeInputFirst: PropTypes.bool,
	dropdownMaxHeight: PropTypes.number,
	addInfoText: PropTypes.bool,
	projectLocked: PropTypes.bool,
};

LabelPicker.defaultProps = {
	placeInputFirst: true,
	dropdownMaxHeight: 250,
};

export default injectIntl(LabelPicker);